import React, { useEffect, useRef, useState } from "react";
import { useApiUrl, useTranslate } from "@pankod/refine-core";
import { Button, Typography } from "@pankod/refine-antd";
import { LoadingOutlined, UploadOutlined, DeleteOutlined } from "@ant-design/icons";
import axios from "axios";

import { IAsset } from "interfaces";
import { useAssetUploadingTracker } from "./AssetUploadingContext";

const { Text } = Typography;

interface AssetUploadFieldProps {
  /** Asset domain, sent to the backend in the `kind` form field. */
  kind: string;
  /** Form.Item-controlled value: the stored Asset (or null). */
  value?: IAsset | null;
  /** Form.Item-controlled onChange callback. */
  onChange?: (asset: IAsset | null) => void;
  /** Comma-separated MIME types accepted by the file input. Defaults to JPEG/PNG/WebP. */
  accept?: string;
  /** Client-side size cap (bytes). Mirrors the backend's 5 MiB default. */
  maxBytes?: number;
}

const DEFAULT_ACCEPT = "image/jpeg,image/png,image/webp";
const DEFAULT_MAX_BYTES = 5 * 1024 * 1024;

const formatBytes = (bytes: number) => {
  if (bytes >= 1024 * 1024) return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
  if (bytes >= 1024) return `${(bytes / 1024).toFixed(0)} KB`;
  return `${bytes} B`;
};

/**
 * Single image-slot uploader matching the contract in
 * docs/01-frontend-integration.md (v2: assets-table refactor).
 *
 * Behavior:
 *  - Click the slot → file picker (filtered by `accept`).
 *  - Validates MIME + size client-side before uploading.
 *  - Shows a local preview (URL.createObjectURL) while the upload is in flight.
 *  - POST multipart/form-data to /api/v2/asset with { file, kind }.
 *  - On success: calls onChange(asset) with the full Asset object so the form
 *    stores it (the serializer later strips it down to {asset_id} on submit).
 *  - On error: keeps the previous value, displays the server message.
 *  - "Remove" sets the slot to null (the v2 contract for "no image" — no more
 *    empty string).
 *
 * Form.Item integration: value/onChange match antd's controlled-input contract,
 * so dropping this inside `<Form.Item name={...}>` Just Works.
 *
 * Reports to AssetUploadingContext while any upload is in progress so the
 * surrounding page can disable Save until everything settles.
 */
export const AssetUploadField: React.FC<AssetUploadFieldProps> = ({
  kind,
  value,
  onChange,
  accept = DEFAULT_ACCEPT,
  maxBytes = DEFAULT_MAX_BYTES,
}) => {
  const t = useTranslate();
  const apiUrl = useApiUrl();
  const APIToken =
    typeof window !== "undefined" ? localStorage.getItem("api-token") : null;
  const { begin, end } = useAssetUploadingTracker();

  const inputRef = useRef<HTMLInputElement>(null);
  const [uploading, setUploading] = useState(false);
  const [localPreview, setLocalPreview] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  // Release the object URL when the preview goes away or component unmounts —
  // otherwise the bytes hang around in memory until the tab is closed.
  useEffect(() => {
    return () => {
      if (localPreview) URL.revokeObjectURL(localPreview);
    };
  }, [localPreview]);

  // v2 contract: the backend ships a ready-to-use `url` on every Asset, so
  // the frontend doesn't construct URLs anymore.
  const remoteURL = value?.url ?? null;
  const displayURL = localPreview || remoteURL;

  const allowedMimes = accept.split(",").map((m) => m.trim()).filter(Boolean);

  const handleFile = async (file: File) => {
    setError(null);

    // Client-side validation mirrors the server's; the server is still the
    // authority (it sniffs the first 512 bytes), but failing fast here is
    // better UX than waiting for a 400 round-trip.
    if (allowedMimes.length && !allowedMimes.includes(file.type)) {
      setError(
        t("asset.error.unsupported_type", { type: file.type || "?" }) ||
          `Unsupported type: ${file.type || "?"}`
      );
      return;
    }
    if (file.size > maxBytes) {
      setError(
        t("asset.error.too_large", { max: formatBytes(maxBytes) }) ||
          `File too large (max ${formatBytes(maxBytes)})`
      );
      return;
    }

    // Local preview while uploading. Clear any prior preview's object URL.
    if (localPreview) URL.revokeObjectURL(localPreview);
    setLocalPreview(URL.createObjectURL(file));
    setUploading(true);
    begin();

    const formData = new FormData();
    formData.append("file", file);
    formData.append("kind", kind);

    try {
      const res = await axios.post<IAsset>(
        `${apiUrl}/asset`,
        formData,
        {
          headers: {
            Authorization: `Bearer ${APIToken}`,
            // Don't set Content-Type — axios computes the multipart boundary.
          },
        }
      );
      // Store the full Asset object — the serializer strips it down to
      // `{asset_id}` on submit. Display still uses `value.url` which the
      // backend hands us.
      onChange?.(res.data);
      // Drop the local preview; the value change will trigger a remote URL render.
      setLocalPreview(null);
    } catch (e: any) {
      const msg =
        e?.response?.data?.message ||
        e?.message ||
        t("asset.error.upload_failed") ||
        "Upload failed";
      setError(msg);
      setLocalPreview(null);
    } finally {
      setUploading(false);
      end();
    }
  };

  const clickPicker = () => {
    if (uploading) return;
    inputRef.current?.click();
  };

  const handleRemove = () => {
    if (uploading) return;
    setError(null);
    setLocalPreview(null);
    // v2 contract: null means "no image" (don't send "" anymore).
    onChange?.(null);
  };

  return (
    <div style={{ display: "flex", alignItems: "flex-start", gap: 12 }}>
      <div
        role="button"
        tabIndex={0}
        onClick={clickPicker}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " ") clickPicker();
        }}
        style={{
          width: 140,
          height: 96,
          border: "1px dashed #d9d9d9",
          borderRadius: 4,
          background: "#fafafa",
          cursor: uploading ? "wait" : "pointer",
          position: "relative",
          overflow: "hidden",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          flex: "0 0 auto",
        }}
      >
        {displayURL ? (
          <img
            src={displayURL}
            alt=""
            style={{ width: "100%", height: "100%", objectFit: "cover" }}
          />
        ) : (
          <div style={{ textAlign: "center", color: "#999", fontSize: 12 }}>
            <UploadOutlined style={{ fontSize: 20 }} />
            <div>{t("asset.upload") || "Upload"}</div>
          </div>
        )}
        {uploading && (
          <div
            style={{
              position: "absolute",
              inset: 0,
              background: "rgba(255,255,255,0.6)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <LoadingOutlined style={{ fontSize: 22 }} />
          </div>
        )}
      </div>

      <div style={{ flex: 1, minWidth: 0 }}>
        <input
          ref={inputRef}
          type="file"
          accept={accept}
          style={{ display: "none" }}
          onChange={(e) => {
            const f = e.target.files?.[0];
            if (f) handleFile(f);
            // Reset so re-picking the same file fires onChange again.
            e.target.value = "";
          }}
        />
        <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
          <Button size="small" onClick={clickPicker} disabled={uploading}>
            {value
              ? t("asset.replace") || "Replace"
              : t("asset.choose_file") || "Choose file"}
          </Button>
          {value && (
            <Button
              size="small"
              danger
              icon={<DeleteOutlined />}
              onClick={handleRemove}
              disabled={uploading}
            >
              {t("asset.remove") || "Remove"}
            </Button>
          )}
        </div>
        {value?.key && (
          <Text
            type="secondary"
            style={{ fontSize: 11, display: "block", marginTop: 4, wordBreak: "break-all" }}
          >
            {value.key}
          </Text>
        )}
        {error && (
          <Text type="danger" style={{ fontSize: 12, display: "block", marginTop: 4 }}>
            {error}
          </Text>
        )}
        <Text type="secondary" style={{ fontSize: 11, display: "block", marginTop: 4 }}>
          {t("asset.hint", { max: formatBytes(maxBytes) }) ||
            `JPEG / PNG / WebP · max ${formatBytes(maxBytes)}`}
        </Text>
      </div>
    </div>
  );
};
